import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { reducers, metaReducers } from './store';
import { environment } from '../environments/environment';
import { RuntimeConfigService } from './core/config/runtime-config.service';
import {
  IdentityApiConfiguration,
  provideIdentityApiConfiguration,
} from './api/identity/identity-api-configuration';
import { AuthEffects } from './store/auth/auth.effects';
import { UserAuthEffects } from './store/user/auth/user-auth.effects';
import { WarehouseEffects } from './store/gse/inventory/warehouse/warehouse.effects';
import { EquipmentEffects } from './store/gse/inventory/equipment/equipment.effects';
import {
  GseApiConfiguration,
  provideGseApiConfiguration,
} from './api/gse/gse-api-configuration';
import {
  ActuatorApiConfiguration,
  provideActuatorApiConfiguration,
} from './api/actuator/actuator-api-configuration';
import { SparePartEffects } from './store/gse/inventory/spare-part/spare-part.effects';
import { ManufacturerEffects } from './store/gse/inventory/lookups/manufacturer/manufacturer.effects';
import { CountryEffects } from './store/gse/inventory/lookups/country/country.effects';

import { DailyCheckinEffects } from './store/gse/maintenance/daily-checkin/daily-checkin.effects';
import {
  AdministrationApiConfiguration,
  provideAdministrationApiConfiguration,
} from './api/administration/administration-api-configuration';
import { ProcurementEffects } from './store/gse/maintenance/procurement/procurement.effects';
import { ServiceRequestEffects } from './store/gse/maintenance/service-request/service-request.effects';
import { JobCardEffects } from './store/gse/maintenance/job-card/job-card.effects';
import { PmNotificationEffects } from './store/gse/maintenance/pm-notification/pm-notification.effects';
import { HodEffects } from './store/gse/inventory/lookups/hod/hod.effects';
import { SupervisorEffects } from './store/gse/inventory/lookups/supervisor/supervisor.effects';
import { TechnicianEffects } from './store/gse/inventory/lookups/technician/technician.effects';
import { PlannerEffects } from './store/gse/inventory/lookups/planner/planner.effects';
import { DelegationEffects } from './store/gse/delegation/delegation.effects';
import { InventoryCorrectionEffects } from './store/gse/maintenance/inventory-correction/inventory-correction.effects';
import { MaintenanceDashboardEffects } from './store/gse/maintenance/maintenance-dashboard/maintenance-dashboard.effects';
import { ReportEffects } from './store/gse/reports/report.effects';
import { HodDashboardEffects } from './store/gse/inventory/hod-dashboard/hod-dashboard.effects';
import { AdminUserDashboardEffects } from './store/administration/user-dashboard/user-dashboard.effects';
import { AdministrationUsersEffects } from './store/administration/users/users.effects';
import { PettyCashEffects } from './store/gse/maintenance/petty-cash/petty-cash.effects';
import { FboLocationsEffects } from './store/administration/fbo-locations/fbo-locations.effects';
import { SpareAllocationEffects } from './store/gse/maintenance/spare-allocation/spare-allocation.effects';
import { EquipmentRentalEffects } from './store/gse/inventory/equipment-rental/equipment-rental.effects';
import {
  IamApiConfiguration,
  provideIamApiConfiguration,
} from './api/iam/iam-api-configuration';
import {
  FboApiConfiguration,
  provideFboApiConfiguration,
} from './api/fbo/fbo-api-configuration';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: false, runCoalescing: true }),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({ onSameUrlNavigation: 'reload' })
    ),
    provideHttpClient(withInterceptors([authInterceptor])),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: false || 'none',
          cssLayer: false,
        },
      },
      ripple: true,
    }),
    provideIdentityApiConfiguration(environment.api.identity),
    provideAdministrationApiConfiguration(environment.api.backend),
    provideGseApiConfiguration(environment.api.backend),
    provideActuatorApiConfiguration(environment.api.backend),
    provideIamApiConfiguration(environment.api.backend),
    provideFboApiConfiguration(environment.api.backend),
    // The API base URLs come from /runtime-config.json (rendered from the container's
    // environment at start), so they must be applied before the first API call.
    provideAppInitializer(async () => {
      const runtime = inject(RuntimeConfigService);
      const identityApi = inject(IdentityApiConfiguration);
      const administrationApi = inject(AdministrationApiConfiguration);
      const gseApi = inject(GseApiConfiguration);
      const actuatorApi = inject(ActuatorApiConfiguration);
      const iamApi = inject(IamApiConfiguration);
      const fboApi = inject(FboApiConfiguration);

      await runtime.load();

      identityApi.rootUrl = runtime.identity;
      administrationApi.rootUrl = runtime.backend;
      gseApi.rootUrl = runtime.backend;
      actuatorApi.rootUrl = runtime.backend;
      iamApi.rootUrl = runtime.backend;
      fboApi.rootUrl = runtime.backend;
    }),
    provideStore(reducers, { metaReducers }),
    provideEffects([
      AuthEffects,
      UserAuthEffects,
      WarehouseEffects,
      EquipmentEffects,
      SparePartEffects,
      ManufacturerEffects,
      CountryEffects,
      ProcurementEffects,
      ServiceRequestEffects,
      JobCardEffects,
      PmNotificationEffects,
      DailyCheckinEffects,
      HodEffects,
      SupervisorEffects,
      TechnicianEffects,
      PlannerEffects,
      DelegationEffects,
      InventoryCorrectionEffects,
      MaintenanceDashboardEffects,
      ReportEffects,
      HodDashboardEffects,
      AdminUserDashboardEffects,
      AdministrationUsersEffects,
      PettyCashEffects,
      FboLocationsEffects,
      SpareAllocationEffects,
      EquipmentRentalEffects,
    ]),

    provideRouterStore(),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: environment.production,
    }),
  ],
};
